import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiHandler } from "../../../helper/ApiHandler";
import SEO from "../../../utils/Seo";

const initial = {
  payment_method_id: "",
  account_email: "",
  bitcoin_wallet: "",
  account: "",
  title: "",
  account_number: "",
  iban: "",
  swift_code: "",
  bank_name: "",
  branch: "",
  country: "",
  city: "",
  ifsc_code: "",
};

const inputClass =
  "w-full px-3 py-2 border border-[#F0DCC8] rounded-lg text-sm text-[#1C1410] focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-colors";

const labelClass = "block mb-1.5 font-medium text-[#4D3E30] text-sm";

const PaymentMethod = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userLoginToken } = useSelector((state) => state.user);

  const [method, setMethod] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [formData, setFormData] = useState(initial);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!userLoginToken) return;

    const fetchData = async () => {
      try {
        const res = await ApiHandler(
          "/get_payment_method.php",
          "GET",
          undefined,
          userLoginToken,
          dispatch,
          navigate
        );

        if (res?.data?.status === "1") {
          setPaymentMethods(res.data.data);
        }

        const bankRes = await ApiHandler(
          "/get_bank_details.php",
          "POST",
          undefined,
          userLoginToken,
          dispatch,
          navigate
        );

        if (bankRes?.data?.status === "1" && bankRes?.data?.data?.[0]) {
          const apiData = bankRes.data.data[0];

          setFormData((prev) => ({
            ...prev,
            ...apiData,
            ifsc_code: apiData.ifsc_code || apiData.IFSC_Code || "", // ✅ SAFE MAPPING
          }));

          setMethod(apiData.payment_method_id || "");
        }
      } catch {
        toast.error("Error loading payment info");
      }
    };

    fetchData();
  }, [userLoginToken]);

  /* ================= SET METHOD ================= */
  useEffect(() => {
    if (method) {
      setFormData((prev) => ({ ...prev, payment_method_id: method }));
    }
  }, [method]);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        payment_method_id: formData.payment_method_id,
        account_email: formData.account_email,
        bitcoin_wallet: formData.bitcoin_wallet,
        title: formData.title,
        account_number: formData.account_number,
        iban: formData.iban,
        swift_code: formData.swift_code,
        bank_name: formData.bank_name,
        branch: formData.branch,
        country: formData.country,
        city: formData.city,

        // ✅ IMPORTANT
        ifsc_code: method === "1" ? formData.ifsc_code : "",
      };

      console.log("FINAL API PAYLOAD:", payload);

      const res = await ApiHandler(
        "/add_payment.php",
        "POST",
        payload,
        userLoginToken,
        dispatch,
        navigate
      );

      if (res?.data?.status === "1") {
        toast.success(res.data.msg || "Payment saved");
        navigate("/user/dashboard");
      } else {
        toast.error(res.data.msg || "Error saving payment");
      }
    } catch (error) {
      console.error("PAYMENT SUBMIT ERROR:", error);
      toast.error("Try again later");
    }
  };

  return (
    <>
      <SEO title="Payment Methods - MyAd360" />

      <div className="flex justify-center py-3 px-3 sm:px-4 min-h-[84vh] bg-[#FFFBF5]">
  <div
    className="my-auto bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-[0_10px_30px_-18px_rgba(22,34,27,0.25)] w-full max-w-md md:max-w-lg"
    style={{ borderTop: "4px solid #D97706" }}
  >
          <h2 className="text-xl sm:text-2xl font-semibold text-center text-[#1C1410] mb-5">
            Withdraw Funds
          </h2>

          <form onSubmit={handleSubmit}>
            <label className={labelClass}>Choose Option</label>

            <select
              className={`${inputClass} mb-4`}
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="" disabled>
                Choose option
              </option>

              {paymentMethods.map((pm, i) => (
                <option key={i} value={pm.id}>
                  {pm.payment_method}
                </option>
              ))}
            </select>

            {/* PAYPAL */}
            {method === "2" && (
              <div className="mb-4">
                <label className={labelClass}>Paypal ID</label>
                <input
                  type="email"
                  name="account_email"
                  className={inputClass}
                  value={formData.account_email}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            {/* BANK TRANSFER */}
            {method === "1" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div className="col-span-1 sm:col-span-2">
                  <label className={labelClass}>Account Name</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className={labelClass}>Account Number</label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>
                {/* 
                <div>
                  <label className={labelClass}>Swift Code</label>
                  <input
                    type="text"
                    name="swift_code"
                    value={formData.swift_code}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div> */}

                <div>
                  <label className={labelClass}>Bank Name</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className={labelClass}>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <input
                    type="text"
                    name="branch"
                    value={formData.branch}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className={labelClass}>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>

                <div className="col-span-1 sm:col-span-2">
                  <label className={labelClass}>Branch Address</label>
                  <input
                    type="text"
                    name="iban"
                    value={formData.iban}
                    onChange={handleChange}
                    className={inputClass}
                    required
                  />
                </div>

                {/* ✅ IFSC FIXED */}
                <div className="col-span-1 sm:col-span-2">
                  <label className={labelClass}>IFSC Code</label>
                  <input
                    type="text"
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="SBIN0001234"
                    pattern="^[A-Z]{4}0[A-Z0-9]{6}$"
                    title="Invalid IFSC code"
                    required
                  />
                </div>
              </div>
            )}

            {/* BITCOIN */}
            {method === "4" && (
              <div className="mb-4">
                <label className={labelClass}>Bitcoin Wallet</label>
                <input
                  type="text"
                  name="bitcoin_wallet"
                  className={inputClass}
                  value={formData.bitcoin_wallet}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            <button
              type="submit"
              disabled={!method}
              className="w-full bg-gradient-to-r from-[#D97706] to-[#EA580C] hover:from-[#EA580C] hover:to-[#D97706] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 sm:py-3 font-medium text-sm rounded-lg transition-all mt-6"
            >
              Submit
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default PaymentMethod;